import React from 'react';
import { Box, Grid, Text, VStack, Icon } from '@chakra-ui/react';
import { Brain, Target, Sprout } from 'lucide-react';
import { motion } from 'framer-motion';
import { SectionContainer } from '../SectionContainer';
import { ScrollReveal } from '../ScrollReveal';

const MotionBox = motion(Box);

const pillars = [
  {
    icon: Brain,
    kicker: 'AGENT STRUCTURE',
    title: 'It Orchestrates Work',
    description:
      'Every organization is built from dozens of parent agents — each composed of specialized micro-agents, orchestrating mini-agents, and supporting micro-services. Simple units, recombined into limitless capability.',
  },
  {
    icon: Target,
    kicker: 'INFRASTRUCTURE',
    title: 'It Builds Its Own Infrastructure',
    description:
      'Each agent provisions the resources it needs to work — databases, compute, pipelines — deploying its own cloud infrastructure on demand.',
  },
  {
    icon: Sprout,
    kicker: 'AGENTDEVELOPER',
    title: 'It Builds Itself',
    description:
      'The founder. It designs, codes, and deploys entirely new agents — and entire new organizations — on demand.',
  },
];

function PillarCard({ pillar, index }) {
  return (
    <ScrollReveal delay={index * 0.15}>
      <MotionBox
        whileHover={{ y: -4 }}
        transition={{ duration: 0.25 }}
        bg="bg.700"
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="18px"
        p={{ base: '28px', md: '36px' }}
        h="100%"
        display="flex"
        flexDir="column"
        gap={4}
        cursor="default"
        role="article"
        aria-label={`${pillar.kicker}: ${pillar.title}`}
        _hover={{
          borderColor: 'brand.500',
          boxShadow: '0 0 0 1px rgba(99,102,241,0.3), 0 8px 32px rgba(99,102,241,0.12)',
        }}
        sx={{ transition: 'all 0.25s ease' }}
      >
        {/* Icon */}
        <Box
          w="48px"
          h="48px"
          borderRadius="12px"
          bg="rgba(99,102,241,0.1)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          aria-hidden="true"
        >
          <Icon as={pillar.icon} color="brand.400" boxSize={6} />
        </Box>

        {/* Kicker */}
        <Text
          fontFamily="'JetBrains Mono', monospace"
          fontWeight="500"
          fontSize="0.75rem"
          textTransform="uppercase"
          letterSpacing="1.5px"
          color="brand.300"
        >
          {pillar.kicker}
        </Text>

        {/* Title */}
        <Text
          fontFamily="'Space Grotesk', sans-serif"
          fontWeight="700"
          fontSize="1.35rem"
          color="text.primary"
          lineHeight="1.2"
        >
          {pillar.title}
        </Text>

        {/* Description */}
        <Text color="text.muted" fontSize="1rem" lineHeight="1.65">
          {pillar.description}
        </Text>
      </MotionBox>
    </ScrollReveal>
  );
}

export default function HowItWorksSection() {
  return (
    <SectionContainer bg="bg.900" id="how-it-works" aria-labelledby="how-works-heading">
      <VStack spacing={{ base: 10, md: 14 }} align="stretch">
        <ScrollReveal>
          <Text
            id="how-works-heading"
            as="h2"
            fontFamily="'Space Grotesk', sans-serif"
            fontWeight="700"
            fontSize={{ base: 'clamp(1.8rem, 5vw, 2rem)', md: 'clamp(2rem, 3.5vw, 2.75rem)' }}
            letterSpacing="-0.02em"
            color="text.primary"
          >
            How it works
          </Text>
        </ScrollReveal>

        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
          gap={{ base: 5, md: 6 }}
        >
          {pillars.map((pillar, i) => (
            <PillarCard key={pillar.kicker} pillar={pillar} index={i} />
          ))}
        </Grid>
      </VStack>
    </SectionContainer>
  );
}
